import type { FC } from 'react';
import { useEffect } from 'react';
import { initializeUI, providerPopupStrategy } from '@firebase-oss/ui-core';
import { FirebaseUIProvider, GoogleSignInButton, SignInAuthScreen } from '@firebase-oss/ui-react';
import '@firebase-oss/ui-styles/dist.min.css';
import LogoStack from '@components/LogoStack';
import AppVersion from '@components/AppVersion';
import { auth } from '../modules/firebase/auth';
import { firebaseApp } from '../modules/firebase/firebaseConfig';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import GlobalStyles from '@mui/material/GlobalStyles';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuthUser } from './FirebaseAuthentication';
import { APP_ENV } from '@modules/consts';

/**
 * Pass the same Auth instance `FirebaseAuthentication` listens on, so its `onAuthStateChanged`
 * fires from a FirebaseUI sign-in. Popup is already the default strategy; it is named here because
 * this app depends on it — a redirect flow would lose the SPA's in-memory state.
 */
const ui = initializeUI({
    app: firebaseApp,
    auth: auth(),
    behaviors: [providerPopupStrategy()],
});

/**
 * FirebaseUI v7's stylesheet is a Tailwind v4 build with no Preflight: it styles fieldsets and
 * buttons through zero-specificity `:where()` rules and assumes the host page has already cleared
 * the user-agent defaults. This app has no `CssBaseline`, so every field group drew a 2px groove
 * fieldset border and the submit button a 2px outset ridge — a box inside a box inside a box.
 *
 * Reset only this subtree. A global baseline would fix it too, but the rest of the app was built
 * without one and would shift.
 */
const firebaseUiSx = {
    '& fieldset': { border: 0, margin: 0, padding: 0, minInlineSize: 0 },
    // Not `.fui-button`: the Google button carries that class too and its border is intentional.
    '& .fui-button:not(.fui-button--secondary)': { border: 0 },
    // The surrounding Paper is already the card, and its own heading is already on the page.
    '& .fui-card': { border: 0, boxShadow: 'none', padding: 0, background: 'transparent' },
    '& .fui-card__header': { display: 'none' },
    // `.fui-form` is `display: block` and spaces its fieldsets with the margins cleared above.
    // 16px matches the divider's own margin, so the whole column keeps one rhythm.
    '& .fui-form': { display: 'flex', flexDirection: 'column', gap: 2 },
};

const LoginPage: FC = () => {
    const navigate = useNavigate();
    const authUser = useAuthUser();
    const theme = useTheme();

    /**
     * FirebaseUI ships its own palette and flips to dark on `prefers-color-scheme: dark`, which
     * would put a black card on this light page for anyone whose OS is in dark mode. Binding its
     * tokens to the MUI theme pins it light and picks up each brand's primary colour.
     *
     * These must land on `:root`. FirebaseUI maps `--color-*: var(--fui-*)` there, and that
     * substitution resolves against `:root`, so overriding `--fui-*` on a descendant has no effect.
     * The doubled `:root:root` outranks the library's own `:root` rule regardless of import order.
     */
    const firebaseUiTheme = {
        '--fui-primary': theme.palette.primary.main,
        '--fui-primary-surface': theme.palette.primary.contrastText,
        '--fui-text': theme.palette.text.primary,
        '--fui-text-muted': theme.palette.text.secondary,
        '--fui-background': theme.palette.background.paper,
        '--fui-border': theme.palette.divider,
        '--fui-input': theme.palette.divider,
        '--fui-error': theme.palette.error.main,
    };

    // Redirect to app if user is already authenticated
    useEffect(() => {
        if (authUser) {
            navigate('/app', { replace: true });
        }
    }, [authUser, navigate]);

    return (
        <Box
            sx={{
                minHeight: '100dvh',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: { xs: 3, sm: 6 },
                background: 'linear-gradient(180deg, #e6f3f3 0%, #f8fafc 30%, #f8fafc 100%)',
            }}
        >
            <Container maxWidth="sm">
                <Paper elevation={3} sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3 }}>
                    {/* Logo and Headings */}
                    <Stack
                        spacing={2}
                        sx={{
                            alignItems: 'center',
                            textAlign: 'center',
                            mb: 2,
                        }}
                    >
                        <LogoStack />
                        <Typography variant="h4" sx={{ fontWeight: 800, color: 'text.primary' }}>
                            Welcome Back
                        </Typography>
                    </Stack>

                    {/* Auth Providers — SignInAuthScreen supplies its own heading and MFA handling. */}
                    <Box sx={{ mx: 'auto', maxWidth: 420, ...firebaseUiSx }}>
                        <GlobalStyles styles={{ ':root:root': firebaseUiTheme }} />
                        <FirebaseUIProvider ui={ui}>
                            <SignInAuthScreen>
                                <GoogleSignInButton />
                            </SignInAuthScreen>
                        </FirebaseUIProvider>
                    </Box>

                    {/* Help / CTA */}
                    <Stack
                        spacing={1}
                        sx={{
                            alignItems: 'center',
                            mt: 4,
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'text.secondary',
                                textAlign: 'center',
                            }}
                        >
                            Don't have access yet?{' '}
                            <Button
                                variant="text"
                                onClick={() => navigate('/request-access')}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 400,
                                    fontSize: 'inherit',
                                    minWidth: 'auto',
                                    p: 0,
                                    textDecoration: 'underline',
                                    color: '#0d0638',
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                        textDecoration: 'underline',
                                    },
                                }}
                                aria-label="Request Access"
                            >
                                Request access here
                            </Button>
                        </Typography>
                        {APP_ENV === 'puppet-interview' ? (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    textAlign: 'center',
                                }}
                            >
                                <Button
                                    variant="text"
                                    href="https://puppetinterview.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Learn more about this app (opens in a new tab)"
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 400,
                                        fontSize: 'inherit',
                                        minWidth: 'auto',
                                        p: 0,
                                        textDecoration: 'underline',
                                        color: '#0d0638',
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                            textDecoration: 'underline',
                                        },
                                    }}
                                >
                                    Learn more about this app
                                </Button>
                            </Typography>
                        ) : null}
                    </Stack>
                </Paper>
                <AppVersion />
            </Container>
        </Box>
    );
};

export default LoginPage;
